import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import COEHeaderComponent from '../../components/coe-header/coe-header.component';
import ProgramCheckoutForm from './program-checkout-form';
import ProgramBillingHistory from '../../components/program-billing/program-billing-history.component';
import useProgramBillingActions from '../../components/program-billing/use-program-billing-actions';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import ProgramsService from '../../services/programs.service';
import TeamsService from '../../services/teams.service';
import {
  BillingInterval,
  BillingPlanCard,
  BillingPlanKey,
  Program,
  ProgramCheckoutResponse,
  ProgramTeamSummary,
} from '../../models/billing.models';
import { palette } from '../../theme';
import { TEAM_DETAILS_STORAGE_KEYS, clearStoredValue } from '../../helpers/cache.helpers';
import {
  accountBillingInterval,
  formatIntervalTotals,
  formatMoney,
  intervalShort,
  isAcademicPlan,
  isTrialExpired,
  isUnlimitedTeamCreator,
  programStatusChip,
  subscriptionTotals,
} from '../../helpers/billing.helpers';
import '../../components/program-billing/program-billing.css';
import './billing.page.css';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

type UnlimitedTeamSummary = {
  id: number;
  name: string;
  status?: string;
  athlete_count?: number;
  role?: string;
};

type PlanChangeFn = (planKey: BillingPlanKey, billingInterval: BillingInterval) => void;

const isCatalogPlan = (planKey: string | null | undefined): boolean =>
  planKey === 'small_team' || planKey === 'program';

const oldestMatching = (programs: Program[], candidates: Program[]): Program | null => {
  for (let index = programs.length - 1; index >= 0; index -= 1) {
    if (candidates.some((candidate) => candidate.id === programs[index].id)) return programs[index];
  }
  return null;
};

/** A plan that is not already on the requested cycle, preferring one that is already being charged. */
const intervalSwitchTarget = (programs: Program[], next: BillingInterval): Program | null => {
  const catalog = programs.filter((program) => isCatalogPlan(program.planKey));
  const mismatched = catalog.filter((program) => program.billingInterval !== next);
  const pool = mismatched.length > 0 ? mismatched : catalog;
  const billable = pool.filter((program) => program.status === 'active' || program.status === 'past_due');
  return oldestMatching(programs, billable.length > 0 ? billable : pool);
};

const lineAmount = (program: Program): number => {
  if (program.includedTeams == null) return program.listPrice ?? 0;
  if (program.extraTeams > 0 && program.extraTeamPrice != null) {
    return (program.listPrice ?? 0) + program.extraTeamPrice * program.extraTeams;
  }
  return program.listPrice ?? 0;
};

const seatCount = (program: Program): number => {
  if (program.includedTeams == null) return Math.max(program.teamCount, 1);
  return Math.max(program.includedTeams + program.extraTeams, 1);
};

const billRenewal = (programs: Program[]): string => {
  const paid = programs.filter(
    (program) => ['active', 'past_due', 'unpaid'].includes(program.status) && program.currentPeriodEnd
  );
  const sample = paid.length > 0 ? paid[paid.length - 1] : programs.find((program) => program.currentPeriodEnd);
  if (sample?.currentPeriodEnd) {
    const ending = paid.length > 0 && paid.every((program) => program.cancelAtPeriodEnd);
    return `${ending ? 'Ends' : 'Renews'} ${dayjs(sample.currentPeriodEnd).format('MMM D, YYYY')}`;
  }
  const trial = [...programs].reverse().find((program) => program.status === 'trialing' && program.trialEndsAt);
  if (trial?.trialEndsAt) return `Trial ends ${dayjs(trial.trialEndsAt).format('MMM D, YYYY')}`;
  return 'Renewal date appears after the first payment';
};

const heroAmount = (programs: Program[]): { amount: string; suffix: string } => {
  const totals = subscriptionTotals(programs);
  if (totals.month > 0 && totals.year > 0) return { amount: formatIntervalTotals(totals), suffix: '' };
  if (totals.year > 0) return { amount: formatMoney(totals.year), suffix: 'per year' };
  if (totals.month > 0) return { amount: formatMoney(totals.month), suffix: 'per month' };
  return { amount: 'Nothing due', suffix: 'until a team converts' };
};

type PlanLineProps = {
  program: Program;
  plans: BillingPlanCard[];
  onProgramChange: (program: Program) => void;
  onProgramEnded: (programId: number) => void;
  bindPlanChange: (programId: number, request: PlanChangeFn) => void;
};

/** One plan on the shared bill: its teams, price, and actions. */
const PlanLine: FC<PlanLineProps> = ({ program, plans, onProgramChange, onProgramEnded, bindPlanChange }) => {
  const programsService = useService(ProgramsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkout, setCheckout] = useState<ProgramCheckoutResponse | null>(null);
  const [moveTeam, setMoveTeam] = useState<ProgramTeamSummary | null>(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [cancelTeam, setCancelTeam] = useState<ProgramTeamSummary | null>(null);
  const [teamActionLoading, setTeamActionLoading] = useState(false);
  const teamsService = useService(TeamsService);
  const trialExpired = isTrialExpired(program);

  const actions = useProgramBillingActions({
    program,
    onProgramChange,
    onProgramEnded: () => {
      clearStoredValue(TEAM_DETAILS_STORAGE_KEYS.TEAM);
      onProgramEnded(program.id);
    },
  });

  const handleCheckout = () => {
    setCheckoutLoading(true);
    programsService
      .checkout(program.id)
      .then((response) => {
        const data = response.data.data;
        setCheckout(data);
        if (data.alreadyActive || !data.clientSecret) {
          showSnackbar('This subscription is already billed', 'success');
        }
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to start checkout', 'error');
      })
      .finally(() => setCheckoutLoading(false));
  };

  bindPlanChange(program.id, actions.requestChangePlan);

  const awaitingConversion = program.status === 'trialing' || program.status === 'incomplete' || trialExpired;
  const cancellable = ['trialing', 'incomplete', 'active', 'past_due'].includes(program.status);
  const chip = programStatusChip(program);
  const chipClass = chip.tone === 'success' ? 'pb-chip' : `pb-chip pb-chip-${chip.tone}`;
  const amount = lineAmount(program);
  const seats = seatCount(program);
  const billedNow = program.status === 'active' || program.status === 'past_due' || program.status === 'unpaid';
  const otherPlanKey: BillingPlanKey | null =
    program.planKey === 'small_team' ? 'program' : program.planKey === 'program' ? 'small_team' : null;
  const otherPlan = plans.find((plan) => plan.key === otherPlanKey);
  const otherPrice = otherPlan
    ? program.billingInterval === 'year'
      ? otherPlan.yearlyPrice
      : otherPlan.monthlyPrice
    : null;
  const canSwitchTeam = Boolean(otherPlanKey) && ['trialing', 'active', 'past_due'].includes(program.status);

  const confirmMove = () => {
    if (!moveTeam || !otherPlanKey) return;
    setMoveLoading(true);
    programsService
      .moveTeamPlan(program.id, moveTeam.id, otherPlanKey)
      .then(() => {
        setMoveTeam(null);
        onProgramChange(program);
        showSnackbar(
          `${moveTeam.name} is now on ${otherPlanKey === 'program' ? 'Large Team' : 'Small Team'}`,
          'success'
        );
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to switch this team', 'error');
      })
      .finally(() => setMoveLoading(false));
  };

  const confirmCancelTeam = () => {
    if (!cancelTeam) return;
    setTeamActionLoading(true);
    teamsService
      .deleteTeam(String(cancelTeam.id))
      .then((response) => {
        const teamName = cancelTeam.name;
        setCancelTeam(null);
        onProgramChange(program);
        showSnackbar(response.data?.message || `${teamName} will not renew.`, 'success');
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to cancel this team', 'error');
      })
      .finally(() => setTeamActionLoading(false));
  };

  const keepTeam = (team: ProgramTeamSummary) => {
    setTeamActionLoading(true);
    teamsService
      .keepTeam(team.id)
      .then(() => {
        onProgramChange(program);
        showSnackbar(`${team.name} stays on the bill`, 'success');
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        showSnackbar(error.response?.data?.message || 'Unable to keep this team', 'error');
      })
      .finally(() => setTeamActionLoading(false));
  };

  const paidPlan = program.status === 'active' || program.status === 'past_due';
  const hidePlanCancel = paidPlan && program.teams.length > 0 && !program.cancelAtPeriodEnd;
  const hidePlanKeep = paidPlan && program.teams.some((team) => team.billingEndsAt);

  return (
    <div className="billing-line">
      <div className="billing-line-top">
        <div>
          <div className="billing-line-name">
            {program.planName}
            <span className={chipClass}>{chip.label}</span>
          </div>
          <div className="billing-line-sub">
            Up to {program.membersPerTeam} members per team
            {program.cancelAtPeriodEnd && program.currentPeriodEnd
              ? ` · ends ${dayjs(program.currentPeriodEnd).format('MMM D, YYYY')}`
              : program.status === 'trialing' && program.trialEndsAt
              ? ` · trial ends ${dayjs(program.trialEndsAt).format('MMM D, YYYY')}`
              : ''}
          </div>
        </div>
        <div className="billing-line-price">
          <b>
            {formatMoney(amount)}
            <span>{intervalShort(program.billingInterval)}</span>
          </b>
          <span>
            {program.teams.length > 0 && program.teams.every((team) => team.billingEndsAt)
              ? 'Drops off at renewal'
              : seats > 1 && program.listPrice != null
              ? `${formatMoney(program.listPrice)} each · ${seats} teams`
              : billedNow
              ? 'On this bill'
              : 'Not billed yet'}
          </span>
        </div>
      </div>

      {program.teams.length > 0 ? (
        <div className="billing-line-teams">
          {program.teams.map((team) => {
            const overCap =
              canSwitchTeam &&
              otherPlan != null &&
              team.memberCount != null &&
              team.memberCount > otherPlan.membersPerTeam;
            const switchLabel =
              !canSwitchTeam || team.billingEndsAt
                ? null
                : program.planKey === 'small_team'
                ? 'Upgrade to Large'
                : 'Move to Small';
            return (
              <div key={team.id} className="billing-team-row">
                <button
                  type="button"
                  className="billing-team-chip"
                  onClick={() => navigate(`/teams/${team.id}/roster`)}
                >
                  <span
                    className="billing-team-swatch"
                    style={{ backgroundColor: team.color || palette.primary.main }}
                  />
                  {team.name}
                  {team.billingEndsAt ? <em>Ends {dayjs(team.billingEndsAt).format('MMM D')}</em> : null}
                </button>
                {switchLabel && overCap ? (
                  <span className="billing-team-block">
                    {otherPlan?.name} allows up to {otherPlan?.membersPerTeam} members. This team has {team.memberCount}
                    .
                  </span>
                ) : switchLabel ? (
                  <button type="button" className="pb-btn pb-btn-secondary" onClick={() => setMoveTeam(team)}>
                    {switchLabel}
                  </button>
                ) : null}
                {paidPlan && team.billingEndsAt ? (
                  <button
                    type="button"
                    className="pb-btn pb-btn-primary"
                    onClick={() => keepTeam(team)}
                    disabled={teamActionLoading}
                  >
                    Keep this team
                  </button>
                ) : paidPlan && !program.cancelAtPeriodEnd ? (
                  <button
                    type="button"
                    className="pb-btn pb-btn-danger"
                    onClick={() => setCancelTeam(team)}
                    disabled={teamActionLoading}
                  >
                    Cancel team
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pb-empty">No team yet</div>
      )}

      {program.status === 'incomplete' ? (
        <div className="pb-callout pb-callout-warning">
          The free trial was already used. Subscribe to activate this team. It stays pending until payment succeeds.
        </div>
      ) : trialExpired ? (
        <div className="pb-callout pb-callout-warning">This free trial has ended. Convert to keep the team active.</div>
      ) : program.status === 'trialing' ? (
        <div className="pb-callout">No card is required until this free trial ends.</div>
      ) : null}

      <div className="pb-foot">
        {awaitingConversion && !checkout?.clientSecret ? (
          <button type="button" className="pb-btn pb-btn-primary" onClick={handleCheckout} disabled={checkoutLoading}>
            {checkoutLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : program.status === 'incomplete' ? (
              'Subscribe to activate'
            ) : trialExpired ? (
              'Convert to paid now'
            ) : (
              'Convert to paid'
            )}
          </button>
        ) : null}
        {cancellable && !(program.cancelAtPeriodEnd ? hidePlanKeep : hidePlanCancel) ? (
          program.cancelAtPeriodEnd ? (
            <button
              type="button"
              className="pb-btn pb-btn-primary pb-foot-end"
              onClick={actions.resumeCancellation}
              disabled={actions.cancelLoading}
            >
              {actions.cancelLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : program.sharesBill ? (
                'Keep this plan'
              ) : (
                'Keep this subscription'
              )}
            </button>
          ) : (
            <button type="button" className="pb-btn pb-btn-danger pb-foot-end" onClick={actions.openCancelDialog}>
              {program.status === 'trialing'
                ? 'End free trial'
                : program.status === 'incomplete'
                ? 'Cancel this team'
                : program.sharesBill
                ? 'Cancel this plan'
                : 'Cancel this subscription'}
            </button>
          )
        ) : null}
      </div>

      {awaitingConversion && checkout?.clientSecret ? (
        <div className="billing-checkout">
          <div className="pb-cap">Card details</div>
          {stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: checkout.clientSecret, appearance: { theme: 'stripe' } }}
            >
              <ProgramCheckoutForm />
            </Elements>
          ) : (
            <Typography color="error">
              Stripe publishable key is missing. Add REACT_APP_STRIPE_PUBLISHABLE_KEY to .env.development and restart
              yarn start:dev.
            </Typography>
          )}
        </div>
      ) : null}

      {actions.dialogs}
      <Dialog
        open={Boolean(cancelTeam)}
        onClose={() => !teamActionLoading && setCancelTeam(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel this team?</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            {cancelTeam?.name} stays available until the end of this billing period and will not renew.
            {program.teams.filter((team) => !team.billingEndsAt).length > 1
              ? ` The other teams on ${program.planName} stay on the bill.`
              : program.sharesBill
              ? ' Other plans on this bill stay.'
              : ''}{' '}
            There is no charge today.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelTeam(null)} disabled={teamActionLoading}>
            Keep this team
          </Button>
          <Button color="error" variant="contained" onClick={confirmCancelTeam} disabled={teamActionLoading}>
            {teamActionLoading ? <CircularProgress size={20} color="inherit" /> : 'Cancel this team'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(moveTeam)} onClose={() => !moveLoading && setMoveTeam(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {program.planKey === 'small_team' ? 'Upgrade this team?' : 'Move this team to Small?'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            {moveTeam?.name} moves to {otherPlan?.name || 'the other plan'} and stays on the{' '}
            {program.billingInterval === 'year' ? 'yearly' : 'monthly'} bill
            {otherPrice != null ? ` at ${formatMoney(otherPrice)}${intervalShort(program.billingInterval)}` : ''}.
          </Typography>
          <Typography sx={{ pt: 1.5 }}>
            {program.status === 'trialing'
              ? 'A free trial stays free. If that plan is already on the paid bill, this team joins it and the rest of this period is charged.'
              : 'The charge today is the price difference for the rest of this period. Other teams keep their plan. Monthly and yearly still switch together for every team.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMoveTeam(null)} disabled={moveLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmMove} disabled={moveLoading}>
            {moveLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm switch'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

type CatalogBillProps = {
  programs: Program[];
  plans: BillingPlanCard[];
  onPrograms: (next: Program[] | ((current: Program[]) => Program[])) => void;
  onReload: () => void;
  onProgramEnded: (programId: number) => void;
};

/** The account bill: one cycle, every plan on it, and one payment history. */
const CatalogBill: FC<CatalogBillProps> = ({ programs, plans, onPrograms, onReload, onProgramEnded }) => {
  const changePlanById = useRef(new Map<number, PlanChangeFn>());
  const [historyKey, setHistoryKey] = useState(0);
  const cycle = accountBillingInterval(programs) || 'month';
  const yearlyOnly = programs.some((program) => isAcademicPlan(program.planKey));
  const ordered = [...programs].reverse();
  const hero = heroAmount(programs);
  const drifted = programs.some((program) => isCatalogPlan(program.planKey) && program.billingInterval !== cycle);
  const unpaid = programs.some((program) => program.status === 'unpaid');
  const pastDue = !unpaid && programs.some((program) => program.status === 'past_due');
  const teamTotal = programs.reduce((sum, program) => sum + program.teamCount, 0);

  const bindPlanChange = (programId: number, request: PlanChangeFn) => {
    changePlanById.current.set(programId, request);
  };

  const askCycle = (next: BillingInterval) => {
    if (yearlyOnly || next === cycle) return;
    const target = intervalSwitchTarget(programs, next);
    const request = target ? changePlanById.current.get(target.id) : undefined;
    if (target && request) request(target.planKey, next);
  };

  const applyProgram = (next: Program) => {
    onPrograms((current) => current.map((item) => (item.id === next.id ? next : item)));
    onReload();
    setHistoryKey((key) => key + 1);
  };

  return (
    <>
      <div className="billing-bill">
        <div className="billing-bill-hero">
          <div>
            <div className="billing-bill-kicker">One bill</div>
            <div className="billing-bill-total">
              {hero.amount}
              {hero.suffix ? <span>{hero.suffix}</span> : null}
            </div>
            <div className="billing-bill-meta">
              {billRenewal(programs)} · {teamTotal} {teamTotal === 1 ? 'team' : 'teams'} · {programs.length}{' '}
              {programs.length === 1 ? 'plan' : 'plans'}
            </div>
          </div>
          <div className="billing-cycle" role="group" aria-label="Billing cycle">
            {yearlyOnly ? null : (
              <button
                type="button"
                className={cycle === 'month' ? 'active' : ''}
                aria-pressed={cycle === 'month'}
                title={cycle === 'month' ? undefined : 'Switches every team to monthly billing'}
                onClick={() => askCycle('month')}
              >
                Monthly
              </button>
            )}
            <button
              type="button"
              className={cycle === 'year' || yearlyOnly ? 'active' : ''}
              aria-pressed={cycle === 'year' || yearlyOnly}
              title={
                yearlyOnly
                  ? 'Academic keeps this bill yearly'
                  : cycle === 'year'
                  ? undefined
                  : 'Switches every team to yearly billing'
              }
              onClick={() => askCycle('year')}
            >
              Yearly
            </button>
          </div>
        </div>

        {unpaid ? (
          <p className="billing-bill-note">
            Payment failed and retries are exhausted. Every team on this bill is locked. Contact support to update your
            card.
          </p>
        ) : null}
        {pastDue ? (
          <p className="billing-bill-note">
            Your last payment failed. Stripe retries the card automatically. Contact support to update your payment
            method.
          </p>
        ) : null}
        {yearlyOnly ? (
          <p className="billing-bill-note billing-bill-note-info">
            Academic is yearly, so Small Team and Large Team on this account stay yearly.
          </p>
        ) : null}
        {drifted ? (
          <p className="billing-bill-note">
            These plans are not on one cycle yet. Choose monthly or yearly above to put them on the same bill.
          </p>
        ) : null}

        <div className="billing-lines">
          {ordered.map((program) => (
            <PlanLine
              key={program.id}
              program={program}
              plans={plans}
              onProgramChange={applyProgram}
              onProgramEnded={onProgramEnded}
              bindPlanChange={bindPlanChange}
            />
          ))}
        </div>
        <p className="billing-bill-foot">
          Cancel a team on its row. The other teams on that plan stay on the bill. Monthly and yearly still switch every
          team together.
        </p>
      </div>
      <ProgramBillingHistory programIds={programs.map((program) => program.id)} refreshKey={historyKey} />
    </>
  );
};

const BillingPage: FC = () => {
  const programsService = useService(ProgramsService);
  const teamsService = useService(TeamsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [plans, setPlans] = useState<BillingPlanCard[]>([]);
  const [unlimitedTeams, setUnlimitedTeams] = useState<UnlimitedTeamSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      programsService
        .listPrograms()
        .then((response) => response.data.data || [])
        .catch(() => [] as Program[]),
      teamsService
        .getTeams()
        .then((response) => {
          const teams = (response.data?.data || []) as Array<UnlimitedTeamSummary & { plan?: string }>;
          return teams.filter(
            (team) => team.plan === 'unlimited' && team.status !== 'cancelled' && team.status !== 'canceled'
          );
        })
        .catch(() => [] as UnlimitedTeamSummary[]),
      programsService
        .getBillingPlans()
        .then((response) => response.data.data)
        .catch(() => null),
    ])
      .then(([openPrograms, legacyTeams, plansResponse]) => {
        if (cancelled) return;
        setPrograms(openPrograms);
        setUnlimitedTeams(legacyTeams);
        setPlans(plansResponse?.plans || []);
      })
      .catch(() => {
        if (!cancelled) showSnackbar('Unable to load billing', 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reloadPrograms = () => {
    programsService.listPrograms().then((response) => {
      setPrograms(response.data.data || []);
    });
  };

  return (
    <div className={window.localStorage.getItem('sidedrawer') ? 'page-full' : 'page'} id="page">
      <COEHeaderComponent title="Billing" />
      <div className="billing-page">
        <div className="billing-shell">
          <div className="billing-heading">
            <div>
              <Typography className="billing-heading-title">Subscription &amp; Billing</Typography>
              <Typography className="billing-heading-sub">
                {programs.length === 0
                  ? isUnlimitedTeamCreator()
                    ? 'Founding Unlimited. New squads are included.'
                    : 'Nothing is billed until you create a team.'
                  : 'Every team on this account shares one bill and one renewal date.'}
              </Typography>
            </div>
            {programs.length > 0 ? (
              <button type="button" className="pb-btn pb-btn-primary" onClick={() => navigate('/create-team')}>
                Add a team
              </button>
            ) : null}
          </div>

          {loading ? (
            <Box className="billing-loading">
              <CircularProgress size={56} />
            </Box>
          ) : programs.length === 0 && (unlimitedTeams.length > 0 || isUnlimitedTeamCreator()) ? (
            <Box className="billing-layout">
              <Box className="billing-column">
                <div className="pb-card">
                  <div className="pb-cap">
                    Current subscription <span className="pb-chip pb-chip-warning">Legacy</span>
                  </div>
                  <div className="pb-plan-name">CoeLab Unlimited</div>
                  <div className="pb-plan-sub">
                    {unlimitedTeams.length === 0
                      ? 'No squads on this plan yet'
                      : `${unlimitedTeams.length} ${
                          unlimitedTeams.length === 1 ? 'team' : 'teams'
                        } on this legacy plan`}
                  </div>
                  <ul className="billing-team-list">
                    {unlimitedTeams.map((team) => (
                      <li key={team.id}>
                        <span className="billing-team-swatch" style={{ backgroundColor: palette.primary.main }} />
                        <span className="billing-team-name">
                          {team.name}
                          {team.athlete_count != null ? ` · ${team.athlete_count} athletes` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="pb-callout">
                    {isUnlimitedTeamCreator()
                      ? 'Add another squad on this plan anytime. Members are included. Moving to Small Team, Large Team, or Academic is still case-by-case with CoeLab.'
                      : 'Seat changes and cancellation stay on Manage Team — Unlimited is closed for new sales. Need Small Team, Large Team, or Academic instead? Contact CoeLab for a case-by-case move.'}
                  </div>
                  <div className="pb-foot">
                    {isUnlimitedTeamCreator() ? (
                      <button type="button" className="pb-btn pb-btn-primary" onClick={() => navigate('/create-team')}>
                        Add a squad
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={`pb-btn ${isUnlimitedTeamCreator() ? 'pb-btn-secondary' : 'pb-btn-primary'}`}
                      onClick={() => navigate('/teams')}
                    >
                      Manage on My Teams
                    </button>
                  </div>
                </div>
              </Box>
              <Box className="billing-column">
                <div className="pb-card">
                  <div className="pb-cap">How this works</div>
                  <div className="pb-pay-title">Stays as-is</div>
                  <div className="pb-plan-sub">
                    Your founding Unlimited subscription keeps working. Use Manage Team on each squad to adjust seats,
                    see payment history, or cancel.
                  </div>
                </div>
              </Box>
            </Box>
          ) : programs.length === 0 ? (
            <div className="billing-bill">
              <div className="billing-bill-hero">
                <div>
                  <div className="billing-bill-kicker">One bill</div>
                  <div className="billing-bill-total">
                    Nothing to bill yet
                    <span>This account has no teams</span>
                  </div>
                </div>
              </div>
              <div className="billing-empty-body">
                <p>
                  A subscription belongs to a team. Create one, then choose Small Team, Large Team, or Academic for that
                  team.
                </p>
                <p>
                  The first team can start a free trial, with no card. Teams added after that join the same monthly or
                  yearly bill.
                </p>
                <button type="button" className="pb-btn pb-btn-primary" onClick={() => navigate('/create-team')}>
                  Create a team
                </button>
              </div>
            </div>
          ) : (
            <CatalogBill
              programs={programs}
              plans={plans}
              onPrograms={setPrograms}
              onReload={reloadPrograms}
              onProgramEnded={(programId) => setPrograms((current) => current.filter((item) => item.id !== programId))}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
